// 🌿 API Craft Back Office - Advanced Foreign Key Select Component
import React, { useState, useRef, useEffect } from 'react';
import { useForeignKeyOptions } from '../hooks/useForeignKeyOptions';
import { ModelField } from '../types';

interface ForeignKeySelectAdvancedProps {
  field: ModelField;
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
}

const ForeignKeySelectAdvanced: React.FC<ForeignKeySelectAdvancedProps> = ({
  field,
  value,
  onChange,
  required = false
}) => {
  const { options, isLoading, error } = useForeignKeyOptions(field.reference_model);
  const [isOpen, setIsOpen] = useState(false);

  const selectRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Function to get display text for an option
  const getDisplayText = (record: any) => {
    const displayFields = ['name', 'title', 'description', 'label', 'email'];
    
    for (const fieldName of displayFields) {
      if (record[fieldName]) {
        return record[fieldName];
      }
    }
    
    return `Record ${record.id.substring(0, 8)}...`;
  };

  // Function to get selected record
  const selectedRecord = options.find(record => record.id === value);
  const displayValue = selectedRecord ? getDisplayText(selectedRecord) : `Select ${field.reference_model || 'record'}`;

  // Function to get all field details for hover
  const getRecordDetails = (record: any) => {
    const details: { [key: string]: any } = {};
    
    Object.keys(record).forEach(key => {
      if (!['created_at', 'updated_at'].includes(key) && record[key] !== null && record[key] !== undefined && record[key] !== '') {
        details[key] = record[key];
      }
    });
    
    return details;
  };

  if (error) {
    return (
      <div className="w-full px-4 py-3 border border-red-300 rounded-lg bg-red-50 text-red-700 text-sm">
        Error loading {field.reference_model} records: {error}
      </div>
    );
  }

  return (
    <div className="relative" ref={selectRef}>
      {/* Main Select Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-left flex items-center justify-between"
      >
        <span className={value ? 'text-forest-900' : 'text-sage-500'}>
          {isLoading ? 'Loading...' : displayValue}
        </span>
        <svg
          className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Options */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-sage-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {/* Empty option */}
          <button
            type="button"
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className="w-full px-4 py-3 text-left hover:bg-mint-50 text-sage-500 border-b border-sage-100"
          >
            {`Select ${field.reference_model || 'record'}`}
          </button>

          {/* Record options */}
          {options.map((record) => (
            <button
              key={record.id}
              type="button"
              onClick={() => {
                onChange(record.id);
                setIsOpen(false);
              }}

              className={`w-full px-4 py-3 text-left hover:bg-mint-50 transition-colors ${
                value === record.id ? 'bg-mint-100 text-mint-800' : 'text-forest-900'
              }`}
            >
              <div className="font-medium">{getDisplayText(record)}</div>
              <div className="text-xs text-sage-500 truncate">ID: {record.id}</div>
              
              {/* Show additional details inline */}
              <div className="text-xs text-sage-400 mt-1 space-y-0.5">
                {Object.entries(getRecordDetails(record))
                  .filter(([key]) => !['id'].includes(key))
                  .slice(0, 2) // Show only first 2 additional fields
                  .map(([key, value]) => (
                    <div key={key} className="flex">
                      <span className="capitalize w-16 flex-shrink-0">{key}:</span>
                      <span className="truncate">{String(value)}</span>
                    </div>
                  ))}
              </div>
            </button>
          ))}

          {options.length === 0 && !isLoading && (
            <div className="px-4 py-6 text-center text-sage-500 text-sm">
              No {field.reference_model} records found. Create some first.
            </div>
          )}
        </div>
      )}



      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <svg className="animate-spin h-4 w-4 text-mint-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
    </div>
  );
};

export default ForeignKeySelectAdvanced;
